import { useEffect } from "react";

export type DocumentMeta = {
  title: string;
  description?: string;
  ogTitle?: string;
  ogDescription?: string;
  /**
   * Absolute or root-relative URL. Defaults to `/og-default.png` (CONTEXT D-36).
   * Phase 7 may add per-course OG image generation.
   */
  ogImage?: string;
  ogUrl?: string;
};

/**
 * Per-page side-effect that owns <title>, <meta name="description">, and OG tags.
 * Idempotent: if a tag already exists in <head>, update it; otherwise create one.
 * No cleanup -- the next page's useDocumentMeta call overwrites.
 *
 * Defaults og:title / og:description from title / description so callers don't
 * have to repeat themselves; og:image defaults to the brand-default placeholder
 * shipped at /og-default.png by plan 02-01.
 */
export function useDocumentMeta(meta: DocumentMeta) {
  useEffect(() => {
    if (meta.title) document.title = meta.title;
    setMeta("description", meta.description, "name");
    setMeta("og:title", meta.ogTitle ?? meta.title, "property");
    setMeta(
      "og:description",
      meta.ogDescription ?? meta.description,
      "property"
    );
    setMeta("og:image", meta.ogImage ?? "/og-default.png", "property");
    setMeta("og:url", meta.ogUrl ?? window.location.href, "property");
  }, [meta]);
}

function setMeta(
  key: string,
  value: string | undefined,
  attr: "name" | "property"
) {
  if (value === undefined) return;
  let el = document.head.querySelector<HTMLMetaElement>(
    `meta[${attr}="${key}"]`
  );
  if (!el) {
    el = document.createElement("meta");
    el.setAttribute(attr, key);
    document.head.appendChild(el);
  }
  el.setAttribute("content", value);
}
